import React, { useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { useController, useForm } from 'react-hook-form'
import { useRecoilValue, useRecoilState } from 'recoil'
import { useRouter } from 'next/router'
import Image from 'next/image'

import '@typeform/embed/build/css/popup.css'

import Button from '@/components/ui/Button/Button'
import FormInput from '@/components/ui/FormInput/FormInput'
import ListboxButton from '@/components/ui/Listbox/ListboxButton'
import ListboxOptions from '@/components/ui/Listbox/ListboxOptions'
import ListboxOption from '@/components/ui/Listbox/ListboxOption'
import CardVenue from '@/components/ui/Card/CardVenue'
import Alert from '@/components/ui/Alert/Alert'
import IconEuro from '@/components/ui/Icon/IconEuro'
import { Listbox } from '@headlessui/react'
import { formResponse, additionalStep } from '../../../store/venueRequest'
import patchVenueAdditionalRequest from '../../../api/patchVenueAdditionalRequest'
import { GetVenuesQuery } from '../../../generated/graphcms'
import useTimeStampMap, { type TimeStamp } from '../../../hooks/useTimeStampMap'
import useErrorHandler from '../../../hooks/useErrorHandler'
import filterUnlistedVenues from '../../../utils/filterUnlistedVenues'

type Venue = GetVenuesQuery['venues'][0]

export interface Props {
  venueName: string
  venues: Venue[]
}

export const REQUEST_SUCCESS_STEP = 'requestSuccessStep'

const VenueFormSuccess: React.FC<Props> = ({ venueName, venues }) => {
  const { t } = useTranslation()
  const timeStampMap = useTimeStampMap()
  const errorHandler = useErrorHandler()

  const router = useRouter()
  const [isFormSent, setIsFormSent] = useState(false)
  const [error, setError] = useState('')

  const [similarVenuesList, setSimilarVenuesList] = useState<Venue[]>([])

  const { similarVenues, moreVenuesUrl, requestUuid } = useRecoilValue(formResponse)
  const [formValues, setFormValues] = useRecoilState(additionalStep)

  const { register, control, setValue, handleSubmit } = useForm({
    defaultValues: formValues,
  })

  const { field: timePeriodArrivalField } = useController({
    name: 'timePeriodArrival',
    control,
  })

  const { field: timePeriodDepartureField } = useController({
    name: 'timePeriodDeparture',
    control,
  })

  useEffect(() => {
    const cachedStepString = localStorage?.getItem(REQUEST_SUCCESS_STEP)

    if (cachedStepString) {
      const cachedStep = JSON.parse(cachedStepString)

      setValue('alternativeDates', cachedStep.alternativeDates)
      setValue('timePeriodArrival', cachedStep.timePeriodArrival)
      setValue('timePeriodDeparture', cachedStep.timePeriodDeparture)
      setValue('preferredBudget', cachedStep.preferredBudget)
    }
  }, [setValue])

  useEffect(() => {
    setSimilarVenuesList(
      filterUnlistedVenues(venues).filter((_venue) =>
        similarVenues.some((similarVenue: Venue) => similarVenue?.slug === _venue.slug)
      )
    )
  }, [venues, similarVenues])

  return (
    <>
      <div className="relative md:pl-14">
        <div className="absolute left-0 top-[-10px] flex hidden flex-col items-center md:block">
          <div className="flex flex-col items-center">
            <Image
              className="relative top-1 transform transition-transform duration-500 ease-in-out"
              width={40}
              height={40}
              src="/images/icons/successCheckIcon.svg"
              alt="check"
            />
            <div
              className={`w-[2px] bg-green-600 transition-all duration-500 ease-in-out ${
                isFormSent ? 'h-[145px]' : 'h-[100px]'
              }`}
            />
            {isFormSent ? (
              <Image
                className="relative bottom-1 transform transition-transform duration-500 ease-in-out"
                width={40}
                height={40}
                src="/images/icons/successCheckIcon.svg"
                alt="check"
              />
            ) : (
              <div className="h-[35px] w-[35px] rounded-full border-2 border-gray-500" />
            )}
            <div
              className={`${
                isFormSent ? 'relative bottom-2 h-[70px] bg-green-600' : 'h-[574px] bg-gray-500'
              } w-[2px] transition-all duration-500 ease-in-out`}
            />
            <div
              className={`${
                isFormSent && 'relative bottom-2'
              } h-[35px] w-[35px] rounded-full border-2 border-gray-500`}
            />
          </div>
        </div>

        <h1 className="mb-4 text-xl font-semibold md:whitespace-nowrap">
          {t('venue.requestForm.successStep.successHeader', { venue: venueName })}
        </h1>
        <p className="w-2/3 text-gray-500 max-xl:w-full">
          {t('venue.requestForm.successStep.successDescription')}
        </p>
        {!isFormSent ? (
          <>
            <h1 className="mb-4 mt-10 text-xl font-semibold">
              {t('venue.requestForm.successStep.additionalForm.additionalFormHeader')}
            </h1>
            <p className="text-gray-500">
              {t('venue.requestForm.successStep.additionalForm.additionalFormDescription')}
            </p>
            <div className="mt-4 flex-1 space-y-4 rounded-lg border border-gray-200 p-4 text-sm md:w-2/3 md:space-y-6 md:px-6 md:py-7 md:text-lg">
              <form
                onSubmit={handleSubmit(async (data) => {
                  try {
                    localStorage.setItem(REQUEST_SUCCESS_STEP, JSON.stringify(data))

                    const res = await patchVenueAdditionalRequest(requestUuid, data)

                    setFormValues(data)

                    if (res?.data?.status === 'success') {
                      setIsFormSent(true)
                    } else {
                      const message = await errorHandler(res.data)
                      setError(message)
                    }
                  } catch (e) {
                    await errorHandler(e, 'Failed to send additional request details')
                  }
                })}
              >
                <h1 className="mb-3 text-xl font-semibold">
                  {t('venue.requestForm.successStep.additionalForm.altDatesInput')}
                </h1>
                <FormInput {...register('alternativeDates')} />
                <h1 className="mb-5 mt-10 text-xl font-semibold">
                  {t('venue.requestForm.successStep.additionalForm.datesInput')}
                </h1>
                <div className="flex flex-col space-y-4 md:flex-row md:space-x-4 md:space-y-0">
                  <div className="flex-1">
                    <p>{t('venue.requestForm.successStep.additionalForm.arrival')}</p>
                    <Listbox
                      onChange={(key: TimeStamp) => timePeriodArrivalField.onChange(key)}
                      value="selected"
                      className="relative"
                      as="div"
                    >
                      {({ open }) => (
                        <>
                          <ListboxButton open={open}>
                            {timeStampMap.get(timePeriodArrivalField.value as TimeStamp)}
                          </ListboxButton>
                          <ListboxOptions>
                            {Array.from(timeStampMap).map((arrivalTime) => (
                              <ListboxOption value={arrivalTime[0]} key={arrivalTime[0]}>
                                {arrivalTime[1]}
                              </ListboxOption>
                            ))}
                          </ListboxOptions>
                        </>
                      )}
                    </Listbox>
                  </div>
                  <div className="flex-1">
                    <p>{t('venue.requestForm.successStep.additionalForm.departure')}</p>
                    <Listbox
                      onChange={(key: TimeStamp) => timePeriodDepartureField.onChange(key)}
                      value="selected"
                      className="relative"
                      as="div"
                    >
                      {({ open }) => (
                        <>
                          <ListboxButton open={open}>
                            {timeStampMap.get(timePeriodDepartureField.value as TimeStamp)}
                          </ListboxButton>
                          <ListboxOptions>
                            {Array.from(timeStampMap).map((departureTime) => (
                              <ListboxOption value={departureTime[0]} key={departureTime[0]}>
                                {departureTime[1]}
                              </ListboxOption>
                            ))}
                          </ListboxOptions>
                        </>
                      )}
                    </Listbox>
                  </div>
                </div>

                <h1 className="mb-3 mt-10 text-xl font-semibold">
                  {t('venue.requestForm.successStep.additionalForm.budgetInput')}
                </h1>
                <div className="flex flex-col space-y-4 md:flex-row md:space-x-4 md:space-y-0">
                  <FormInput
                    type="number"
                    min="0"
                    onWheel={(e) => (e.target as HTMLInputElement).blur()}
                    Icon={IconEuro}
                    {...register('preferredBudget')}
                  />
                  <span className="relative w-full text-gray-400">
                    {t('venue.requestForm.successStep.additionalForm.personBudgetInput')}
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <Button className="mt-7" type="submit" variant="secondary" size="xl">
                    {t('venue.requestForm.successStep.additionalForm.saveButton')}
                  </Button>
                  <Button
                    onClick={() => setIsFormSent(true)}
                    className="mt-7"
                    variant="white"
                    size="xl"
                  >
                    {t('venue.requestForm.successStep.additionalForm.skipButton')}
                  </Button>
                  <div className="max-h-2">
                    {error && <Alert>{t('apiErrors.general_error')}</Alert>}
                  </div>
                </div>
              </form>
            </div>
          </>
        ) : (
          <h1 className="my-20 text-xl font-semibold">
            {t('venue.requestForm.successStep.additionalInfoSuccess')}
          </h1>
        )}
        <h1 className="mb-4 mt-8 text-xl font-semibold">
          {t('venue.requestForm.successStep.exploreHeader')}
        </h1>
        {similarVenuesList.length > 0 && (
          <>
            <p className="text-gray-500">{t('venue.requestForm.successStep.exploreDescription')}</p>
            <h1 className="mt-4 text-lg font-semibold">
              {t('venue.requestForm.successStep.venuesHeader')}
            </h1>
          </>
        )}
      </div>

      {similarVenuesList.length > 0 && (
        <div className="mb-10 mt-4 max-w-[1142px] md:pl-14">
          <div className="flex w-full flex-col justify-items-start gap-5 md:flex-row">
            {similarVenuesList.map((similarVenue) => (
              <div className="w-full md:w-[300px]" key={similarVenue.slug}>
                <CardVenue venue={similarVenue} />
              </div>
            ))}
          </div>
        </div>
      )}

      <Button
        onClick={() => router.push(moreVenuesUrl)}
        type="button"
        variant="secondary"
        size="xl"
        className="mt-5 md:ml-14 md:w-[400px]"
      >
        {t('venue.requestForm.successStep.exploreMoreVenuesButton')}
      </Button>
    </>
  )
}

export default VenueFormSuccess
